import { useState, useEffect } from 'react';
import { useTheme } from './theme';
import { db } from './dbClient';
import { useToast } from './Toast';

export default function BulkCampaignWorkflow({ company }) {
  const C = useTheme();
  const { showToast } = useToast();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [scenarios, setScenarios] = useState([]);
  const [currentReport, setCurrentReport] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    campaign_type: 'sanctions_review',
    description: '',
    target_scenario: '',
    filter_risk_level: 'all',
    filter_status: 'open',
    target_entity_type: 'customers', // customers, third_parties, both
  });

  useEffect(() => {
    if (company?.id) {
      loadCampaigns();
      loadScenarios();
    }
  }, [company?.id]);

  async function loadCampaigns() {
    if (!company?.id) return;
    try {
      setLoading(true);
      const { data, error } = await db
        .from('bulk_campaigns')
        .select('*')
        .eq('company_id', company.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setCampaigns(data || []);
    } catch (err) {
      showToast('Error loading campaigns', 'error');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  async function loadScenarios() {
    if (!company?.id) return;
    try {
      const { data, error } = await db
        .from('scenario_types')
        .select('id, code, name')
        .eq('company_id', company.id)
        .order('name');

      if (error) throw error;
      setScenarios(data || []);
    } catch (err) {
      console.error('Error loading scenarios:', err);
    }
  }

  async function handleCreateCampaign() {
    if (!formData.name.trim()) {
      showToast('Campaign name is required', 'error');
      return;
    }

    try {
      // Create campaign
      const { data: campaignData, error: campaignError } = await db
        .from('bulk_campaigns')
        .insert([{
          company_id: company.id,
          name: formData.name,
          campaign_type: formData.campaign_type,
          description: formData.description,
          target_scenario: formData.target_scenario || null,
          filter_criteria: JSON.stringify({
            risk_level: formData.filter_risk_level,
            status: formData.filter_status,
          }),
          status: 'draft',
        }]);

      if (campaignError) throw campaignError;

      showToast('Campaign created successfully', 'success');
      setFormData({
        name: '',
        campaign_type: 'sanctions_review',
        description: '',
        target_scenario: '',
        filter_risk_level: 'all',
        filter_status: 'open',
      });
      setShowForm(false);
      await loadCampaigns();
    } catch (err) {
      showToast('Error creating campaign', 'error');
      console.error(err);
    }
  }

  async function handleLaunchCampaign(id) {
    try {
      const campaign = campaigns.find(c => c.id === id);
      if (!campaign) return;

      let targetCount = 0;

      // Determine what to screen based on campaign type - all query approved KYC submissions
      const { data: kycSubmissions, error: kycError } = await db
        .from('kyc_submissions')
        .select('id')
        .eq('company_id', company.id)
        .eq('status', 'approved');

      if (kycError) throw kycError;
      targetCount = kycSubmissions?.length || 0;

      // Update campaign with target count and launch it
      const { error } = await db
        .from('bulk_campaigns')
        .update({
          status: 'active',
          started_at: new Date().toISOString(),
          num_cases_selected: targetCount
        })
        .eq('id', id);

      if (error) throw error;
      showToast(`Campaign launched - ${targetCount} ${campaign.campaign_type === 'sanctions_review' ? 'customers' : 'entities'} to screen`, 'success');
      loadCampaigns();
    } catch (err) {
      showToast('Error launching campaign', 'error');
      console.error(err);
    }
  }

  async function dilisenseCheck(name, type) {
    const PROXY_BASE = import.meta.env.VITE_PROXY_URL || "http://localhost:3001";
    const endpoint = type === "Business" || type === "Entity" ? "entity" : "individual";
    const url = `${PROXY_BASE}/api/dilisense/${endpoint}?names=${encodeURIComponent(name)}&fuzzy_search=1`;
    const resp = await fetch(url, { method: "GET" });
    if (resp.status === 401) throw new Error("Dilisense API key not configured on server");
    if (resp.status === 400) throw new Error("Bad request — check the customer name");
    if (!resp.ok) throw new Error(`Dilisense API error: ${resp.status}`);
    return resp.json();
  }

  async function runExternalResearch(customer) {
    const PROXY_BASE = import.meta.env.VITE_PROXY_URL || "http://localhost:3001";
    const allResults = {
      web: null,
      news: null,
      court: null,
      social: null,
      hibp: null,
      phone: null
    };

    try {
      // Web search for business/person
      const webResp = await fetch(`${PROXY_BASE}/api/websearch`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ query: customer.name, type: customer.type || 'Individual', num: 8 })
      });
      if (webResp.ok) {
        const data = await webResp.json();
        allResults.web = data.results || [];
      }

      // Adverse news search
      const newsResp = await fetch(`${PROXY_BASE}/api/websearch`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          query: `"${customer.name}" fraud scandal lawsuit sanction`,
          type: 'News',
          num: 5
        })
      });
      if (newsResp.ok) {
        const data = await newsResp.json();
        allResults.news = data.results || [];
      }

      // Court records search
      const courtResp = await fetch(`${PROXY_BASE}/api/websearch`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          query: `site:pacer.uscourts.gov OR site:courtlistener.com "${customer.name}"`,
          type: 'Legal',
          num: 5
        })
      });
      if (courtResp.ok) {
        const data = await courtResp.json();
        allResults.court = data.results || [];
      }

      // Social media profiles
      const socialResp = await fetch(`${PROXY_BASE}/api/websearch`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          query: `site:linkedin.com OR site:twitter.com OR site:facebook.com "${customer.name}"`,
          type: 'Social',
          num: 5
        })
      });
      if (socialResp.ok) {
        const data = await socialResp.json();
        allResults.social = data.results || [];
      }

      // Email breach check if email available
      if (customer.email) {
        const hibpResp = await fetch(`${PROXY_BASE}/api/hibp`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: customer.email })
        });
        if (hibpResp.ok) {
          const data = await hibpResp.json();
          allResults.hibp = data;
        }
      }

      // Phone validation if phone available
      if (customer.phone) {
        const phoneResp = await fetch(`${PROXY_BASE}/api/phonelookup`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ phone: customer.phone })
        });
        if (phoneResp.ok) {
          const data = await phoneResp.json();
          allResults.phone = data;
        }
      }

      // AI synthesis
      const synthesis = await fetch(`${PROXY_BASE}/api/ai`, {
        method: "POST",
        signal: AbortSignal.timeout(90000),
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          system: "You are a senior AML/KYC compliance analyst. Be direct, specific, and actionable. Plain text only — no markdown symbols or asterisks.",
          messages: [{
            role: "user",
            content: buildAISynthesisPrompt(customer, allResults)
          }],
          max_tokens: 1000
        })
      });

      let synthesisText = "";
      if (synthesis.ok) {
        const data = await synthesis.json();
        synthesisText = data.content?.[0]?.text || data.choices?.[0]?.message?.content || "";
      }

      return {
        customer_id: customer.id,
        customer_name: customer.name,
        status: 'completed',
        web_results: allResults.web?.length || 0,
        news_results: allResults.news?.length || 0,
        court_results: allResults.court?.length || 0,
        social_results: allResults.social?.length || 0,
        email_breaches: allResults.hibp?.breaches?.length || 0,
        phone_valid: allResults.phone?.phone_validation?.is_valid ? 'yes' : 'no',
        phone_risk: allResults.phone?.phone_risk?.risk_level || 'unknown',
        ai_summary: synthesisText,
        all_results: allResults
      };
    } catch (err) {
      return {
        customer_id: customer.id,
        customer_name: customer.name,
        status: 'error',
        error: err.message,
        ai_summary: `Research failed: ${err.message}`
      };
    }
  }

  function buildAISynthesisPrompt(customer, allResults) {
    const format = (label, items) => {
      if (!items?.length) return `${label}: No results found.`;
      return `${label}:\n${items.slice(0, 5).map(i => `  • [${i.source || ""}] ${i.title}: ${i.snippet?.slice(0, 180) || ""}`).join("\n")}`;
    };

    return `You are a senior AML/KYC compliance analyst. You have run external OSINT research on the following subject and received the results below. Provide a structured risk assessment.

SUBJECT: ${customer.name}
TYPE: ${customer.type || "Individual"}
COUNTRY: ${customer.country || "Unknown"}

RESEARCH FINDINGS:
${format("Web / Business Search", allResults.web)}
${format("Adverse News & Negative Media", allResults.news)}
${format("Court Records & Legal Proceedings", allResults.court)}
${format("Social Media Profiles", allResults.social)}
${allResults.hibp ? (allResults.hibp.clean ? "Email Breach Check (HIBP): No breaches found." : `Email Breach Check (HIBP): ${allResults.hibp.breaches.length} breach(es) found — ${allResults.hibp.breaches.slice(0, 5).map(b => b.Name).join(", ")}`) : "Email Breach Check: Not run."}
${allResults.phone ? `Phone Intelligence: ${allResults.phone.phone_validation?.is_valid ? `Valid — ${allResults.phone.phone_carrier?.line_type || ""} line, carrier: ${allResults.phone.phone_carrier?.name || ""}, country: ${allResults.phone.phone_location?.country_name || ""}, risk: ${allResults.phone.phone_risk?.risk_level || "unknown"}${allResults.phone.phone_validation?.is_voip ? ", VoIP" : ""}` : "Invalid/unallocated number"}` : "Phone Intelligence: Not run."}

Provide your assessment in this exact structure:
1. OVERALL RISK RATING: [Low / Medium / High / Critical] — one sentence justification
2. KEY FINDINGS: bullet list of the most significant findings (3–6 bullets)
3. ADVERSE MEDIA SUMMARY: summarise any negative news, regulatory actions, or criminal allegations found
4. RECOMMENDED ACTIONS: 2–4 specific compliance actions

Be concise and precise. Flag clearly if no adverse findings were found.`;
  }

  async function handleCompleteCampaign(id) {
    try {
      const campaign = campaigns.find(c => c.id === id);
      if (!campaign) return;

      showToast('Campaign execution started...', 'info');

      // Get customers to screen from approved KYC submissions
      const { data: kycSubmissions, error: customerError } = await db
        .from('kyc_submissions')
        .select('id, first_name, last_name, email, phone, country, type, business_name, status')
        .eq('company_id', company.id)
        .eq('status', 'approved');

      if (customerError) throw customerError;
      const customerList = (kycSubmissions || []).map(k => ({
        id: k.id,
        name: k.type === 'business'
          ? (k.business_name || 'Unknown Business')
          : ([k.first_name, k.last_name].filter(Boolean).join(' ') || 'Unknown'),
        type: k.type === 'business' ? 'Business' : 'Individual',
        email: k.email,
        phone: k.phone,
        country: k.country
      }));

      let screeningResults = [];
      let totalProcessed = 0;
      let flaggedCount = 0;

      if (campaign.campaign_type === 'sanctions_review' || campaign.campaign_type === 'pep_review') {
        // Execute Dilisense screening for each customer
        for (const customer of customerList) {
          try {
            const result = await dilisenseCheck(customer.name, customer.type || 'Individual');
            const records = result.found_records || [];

            let hasHit = false;
            let hitTypes = [];

            if (campaign.campaign_type === 'sanctions_review') {
              const sanctions = records.filter(r => r.source_type === "SANCTION");
              if (sanctions.length > 0) {
                hasHit = true;
                hitTypes.push(`${sanctions.length} sanction hit(s)`);
                flaggedCount++;
              }
            } else if (campaign.campaign_type === 'pep_review') {
              const pep = records.filter(r => r.source_type === "PEP");
              if (pep.length > 0) {
                hasHit = true;
                hitTypes.push(`${pep.length} PEP hit(s)`);
                flaggedCount++;
              }
            }

            screeningResults.push({
              customer_id: customer.id,
              customer_name: customer.name,
              status: hasHit ? 'flagged' : 'cleared',
              findings: hitTypes.length > 0 ? hitTypes.join(', ') : 'No hits',
              total_records: result.total_hits || 0,
              records
            });
            totalProcessed++;
          } catch (err) {
            console.error(`Error screening ${customer.name}:`, err);
            screeningResults.push({
              customer_id: customer.id,
              customer_name: customer.name,
              status: 'error',
              findings: 'Screening failed',
              error: err.message
            });
            totalProcessed++;
          }
        }
      } else if (campaign.campaign_type === 'external_research') {
        // Execute OSINT research for each customer
        for (const customer of customerList) {
          try {
            const researchResult = await runExternalResearch(customer);

            // Determine risk level from AI synthesis
            let riskLevel = 'Unknown';
            if (researchResult.ai_summary) {
              if (researchResult.ai_summary.includes('CRITICAL')) riskLevel = 'Critical';
              else if (researchResult.ai_summary.includes('High')) riskLevel = 'High';
              else if (researchResult.ai_summary.includes('Medium')) riskLevel = 'Medium';
              else if (researchResult.ai_summary.includes('Low')) riskLevel = 'Low';
            }

            screeningResults.push({
              customer_id: customer.id,
              customer_name: customer.name,
              status: 'completed',
              findings: `${researchResult.web_results} web + ${researchResult.news_results} news + ${researchResult.court_results} court results, ${researchResult.email_breaches} email breaches`,
              risk_level: riskLevel,
              ai_summary: researchResult.ai_summary,
              research_stats: {
                web: researchResult.web_results,
                news: researchResult.news_results,
                court: researchResult.court_results,
                social: researchResult.social_results,
                breaches: researchResult.email_breaches,
                phone_risk: researchResult.phone_risk
              }
            });

            // Count high/critical risk as flagged
            if (riskLevel === 'High' || riskLevel === 'Critical') {
              flaggedCount++;
            }

            totalProcessed++;
          } catch (err) {
            console.error(`Error researching ${customer.name}:`, err);
            screeningResults.push({
              customer_id: customer.id,
              customer_name: customer.name,
              status: 'error',
              findings: 'Research failed',
              error: err.message
            });
            totalProcessed++;
          }
        }
      }

      // Save screening/research audit logs (optional - don't block campaign if audit fails)
      for (const result of screeningResults) {
        if (result.status !== 'error') {
          try {
            const action = campaign.campaign_type === 'external_research' ? 'external_research_run' : 'sanctions_screening_run';
            const details = campaign.campaign_type === 'external_research'
              ? {
                  campaign_id: id,
                  campaign_name: campaign.name,
                  status: result.status,
                  findings: result.findings,
                  risk_level: result.risk_level,
                  research_stats: result.research_stats,
                  ai_summary: result.ai_summary
                }
              : {
                  campaign_id: id,
                  campaign_name: campaign.name,
                  status: result.status,
                  findings: result.findings,
                  total_records: result.total_records
                };

            await db
              .from('audit_logs')
              .insert([{
                company_id: company.id,
                action: action,
                entity_id: result.customer_id,
                entity_type: 'customer',
                details: JSON.stringify(details),
                created_by: 'bulk_campaign'
              }]);
          } catch (auditErr) {
            console.warn(`Audit log save failed for ${result.customer_name}:`, auditErr);
            // Don't fail the campaign if audit log fails
          }
        }
      }

      // Generate comprehensive report
      const flaggedCustomers = screeningResults.filter(r => r.status === 'flagged' || (r.risk_level && ['High', 'Critical'].includes(r.risk_level)));
      const clearedCustomers = screeningResults.filter(r => r.status === 'cleared' || r.status === 'completed');
      const errorCustomers = screeningResults.filter(r => r.status === 'error');

      // Calculate research statistics if external research campaign
      let researchStats = {};
      if (campaign.campaign_type === 'external_research') {
        researchStats = {
          total_web_hits: screeningResults.reduce((sum, c) => sum + (c.research_stats?.web || 0), 0),
          total_news_hits: screeningResults.reduce((sum, c) => sum + (c.research_stats?.news || 0), 0),
          total_court_hits: screeningResults.reduce((sum, c) => sum + (c.research_stats?.court || 0), 0),
          total_breaches: screeningResults.reduce((sum, c) => sum + (c.research_stats?.breaches || 0), 0),
          high_risk_customers: flaggedCustomers.length
        };
      }

      const report = {
        campaign_id: id,
        campaign_name: campaign.name,
        campaign_type: campaign.campaign_type,
        status: 'completed',
        cases_processed: totalProcessed,
        created_date: new Date(campaign.created_at).toLocaleDateString(),
        completed_date: new Date().toLocaleDateString(),
        findings: {
          total_screened: totalProcessed,
          flagged: flaggedCount,
          cleared: clearedCustomers.length,
          errors: errorCustomers.length,
          flagged_percentage: totalProcessed > 0 ? ((flaggedCount / totalProcessed) * 100).toFixed(1) : 0
        },
        research_stats: researchStats,
        flagged_customers: flaggedCustomers,
        screening_results: screeningResults,
        recommendations: flaggedCount > 0 ? [
          `Review ${flaggedCount} high-risk customer(s) for enhanced due diligence`,
          'Review AI-generated risk assessments in detail',
          'Document findings and remediation actions',
          'Update customer risk ratings'
        ] : [
          'All customers within normal risk parameters',
          'Archive campaign for compliance records',
          'Update compliance calendar'
        ]
      };

      // Update campaign status
      const { error } = await db
        .from('bulk_campaigns')
        .update({
          status: 'completed',
          completed_at: new Date().toISOString(),
          num_cases_processed: totalProcessed
        })
        .eq('id', id);

      if (error) throw error;

      // Show report in modal
      setCurrentReport(report);
      showToast(`Campaign completed - ${flaggedCount} flagged, ${clearedCustomers.length} cleared`, 'success');
      loadCampaigns();
    } catch (err) {
      showToast('Error completing campaign', 'error');
      console.error(err);
    }
  }

  const getStatusColor = (status) => {
    const colors = {
      draft: C.textDim,
      active: C.accent,
      completed: '#4CAF50',
      cancelled: '#FF6B6B',
    };
    return colors[status] || C.textMid;
  };

  const getCampaignTypeLabel = (type) => {
    const labels = {
      sanctions_review: '⚠️ Sanctions Review',
      pep_review: '👤 PEP Review',
      external_research: '🔍 External Research',
      periodic_review: '🔄 Periodic Review',
      thematic: '🎯 Thematic Review',
    };
    return labels[type] || type;
  };

  if (loading) {
    return <div style={{ padding: '2rem', color: C.textDim }}>Loading campaigns...</div>;
  }

  return (
    <div style={{ padding: '2rem' }}>
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ color: C.text, fontFamily: "'Syne', sans-serif", fontSize: 26, fontWeight: 700, marginBottom: '0.75rem' }}>
          Bulk Campaign Workflow
        </h1>
        <p style={{ color: C.textMid, fontSize: '0.9rem', lineHeight: 1.6 }}>
          Run bulk reviews and campaigns on filtered sets of cases for thematic reviews, sanctions rescreenings, and compliance campaigns.
        </p>
      </div>

      {!showForm && (
        <button
          onClick={() => setShowForm(true)}
          style={{
            padding: '0.6rem 1.2rem',
            background: `${C.accent}22`,
            color: C.accent,
            border: `1px solid ${C.accent}`,
            borderRadius: 6,
            fontWeight: 700,
            cursor: 'pointer',
            fontFamily: "'DM Mono', monospace",
            fontSize: '0.85rem',
            marginBottom: '2rem'
          }}
        >
          + New Campaign
        </button>
      )}

      {showForm && (
        <div style={{
          background: C.surface,
          border: `1px solid ${C.border}`,
          borderRadius: 8,
          padding: '1.5rem',
          marginBottom: '2rem'
        }}>
          <h3 style={{ color: C.text, marginBottom: '1rem', fontFamily: "'Syne', sans-serif" }}>
            Create New Campaign
          </h3>

          <div style={{ display: 'grid', gap: '1rem', marginBottom: '1.5rem' }}>
            <div>
              <label style={{ color: C.textMid, fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>
                Campaign Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="e.g., Q4 2026 Sanctions Rescreening"
                style={{
                  width: '100%',
                  padding: '0.6rem',
                  background: C.bg,
                  border: `1px solid ${C.border}`,
                  borderRadius: 6,
                  color: C.text,
                  fontFamily: "'DM Mono', monospace",
                  fontSize: '0.9rem'
                }}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <label style={{ color: C.textMid, fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>
                  Campaign Type
                </label>
                <select
                  value={formData.campaign_type}
                  onChange={(e) => setFormData({...formData, campaign_type: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '0.6rem',
                    background: C.bg,
                    border: `1px solid ${C.border}`,
                    borderRadius: 6,
                    color: C.text,
                    fontFamily: "'DM Mono', monospace",
                    fontSize: '0.9rem'
                  }}
                >
                  <option value="sanctions_review">Sanctions Review</option>
                  <option value="pep_review">PEP Review</option>
                  <option value="external_research">External Research</option>
                  <option value="periodic_review">Periodic Review</option>
                  <option value="thematic">Thematic Review</option>
                </select>
              </div>

              {scenarios.length > 0 && (
                <div>
                  <label style={{ color: C.textMid, fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>
                    Target Scenario (optional)
                  </label>
                  <select
                    value={formData.target_scenario}
                    onChange={(e) => setFormData({...formData, target_scenario: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '0.6rem',
                      background: C.bg,
                      border: `1px solid ${C.border}`,
                      borderRadius: 6,
                      color: C.text,
                      fontFamily: "'DM Mono', monospace",
                      fontSize: '0.9rem'
                    }}
                  >
                    <option value="">All Scenarios</option>
                    {scenarios.map(scenario => (
                      <option key={scenario.id} value={scenario.id}>{scenario.name}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <label style={{ color: C.textMid, fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>
                  Risk Level Filter
                </label>
                <select
                  value={formData.filter_risk_level}
                  onChange={(e) => setFormData({...formData, filter_risk_level: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '0.6rem',
                    background: C.bg,
                    border: `1px solid ${C.border}`,
                    borderRadius: 6,
                    color: C.text,
                    fontFamily: "'DM Mono', monospace",
                    fontSize: '0.9rem'
                  }}
                >
                  <option value="all">All Levels</option>
                  <option value="CRITICAL">Critical</option>
                  <option value="HIGH">High & Above</option>
                  <option value="MEDIUM">Medium & Above</option>
                </select>
              </div>

              <div>
                <label style={{ color: C.textMid, fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>
                  Case Status
                </label>
                <select
                  value={formData.filter_status}
                  onChange={(e) => setFormData({...formData, filter_status: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '0.6rem',
                    background: C.bg,
                    border: `1px solid ${C.border}`,
                    borderRadius: 6,
                    color: C.text,
                    fontFamily: "'DM Mono', monospace",
                    fontSize: '0.9rem'
                  }}
                >
                  <option value="all">All Status</option>
                  <option value="open">Open</option>
                  <option value="under_review">Under Review</option>
                  <option value="closed">Closed</option>
                </select>
              </div>
            </div>

            <div>
              <label style={{ color: C.textMid, fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Campaign objectives and notes..."
                style={{
                  width: '100%',
                  padding: '0.6rem',
                  background: C.bg,
                  border: `1px solid ${C.border}`,
                  borderRadius: 6,
                  color: C.text,
                  fontFamily: "'DM Mono', monospace",
                  fontSize: '0.9rem',
                  minHeight: '80px',
                  resize: 'vertical'
                }}
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button
              onClick={handleCreateCampaign}
              style={{
                padding: '0.6rem 1.2rem',
                background: C.accent,
                color: '#0A0E1A',
                border: 'none',
                borderRadius: 6,
                fontWeight: 700,
                cursor: 'pointer',
                fontFamily: "'DM Mono', monospace",
                fontSize: '0.85rem'
              }}
            >
              Create Campaign
            </button>
            <button
              onClick={() => setShowForm(false)}
              style={{
                padding: '0.6rem 1.2rem',
                background: 'transparent',
                border: `1px solid ${C.border}`,
                color: C.textMid,
                borderRadius: 6,
                cursor: 'pointer',
                fontFamily: "'DM Mono', monospace",
                fontSize: '0.85rem'
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Campaigns List */}
      <div style={{ display: 'grid', gap: '1rem' }}>
        {campaigns.length === 0 ? (
          <div style={{
            background: C.surface,
            border: `1px solid ${C.border}`,
            borderRadius: 8,
            padding: '2rem',
            textAlign: 'center',
            color: C.text
          }}>
            No campaigns yet. Create one to get started.
          </div>
        ) : (
          campaigns.map(campaign => (
            <div
              key={campaign.id}
              style={{
                background: C.surface,
                border: `1px solid ${C.border}`,
                borderRadius: 8,
                padding: '1.25rem'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1rem' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ color: C.text, fontWeight: 700, fontSize: '1rem', marginBottom: '0.5rem' }}>
                    {campaign.name}
                    <span style={{
                      background: getStatusColor(campaign.status) + '22',
                      color: getStatusColor(campaign.status),
                      fontSize: '0.65rem',
                      fontWeight: 700,
                      padding: '0.3rem 0.5rem',
                      borderRadius: 3,
                      marginLeft: '0.5rem',
                      textTransform: 'uppercase'
                    }}>
                      {campaign.status}
                    </span>
                  </div>
                  <div style={{ color: C.textMid, fontSize: '0.85rem' }}>
                    {getCampaignTypeLabel(campaign.campaign_type)}
                  </div>
                  {campaign.description && (
                    <div style={{ color: C.textMid, fontSize: '0.8rem', marginTop: '0.5rem', fontStyle: 'italic' }}>
                      {campaign.description}
                    </div>
                  )}
                </div>

                {campaign.status === 'draft' && (
                  <button
                    onClick={() => handleLaunchCampaign(campaign.id)}
                    style={{
                      padding: '0.4rem 0.8rem',
                      background: `${C.accent}22`,
                      color: C.accent,
                      border: `1px solid ${C.accent}`,
                      borderRadius: 4,
                      cursor: 'pointer',
                      fontSize: '0.75rem',
                      fontFamily: "'DM Mono', monospace",
                      fontWeight: 600,
                      marginRight: '0.5rem'
                    }}
                  >
                    Launch
                  </button>
                )}
                {campaign.status === 'active' && (
                  <button
                    onClick={() => handleCompleteCampaign(campaign.id)}
                    style={{
                      padding: '0.4rem 0.8rem',
                      background: '#4CAF5022',
                      color: '#4CAF50',
                      border: `1px solid #4CAF50`,
                      borderRadius: 4,
                      cursor: 'pointer',
                      fontSize: '0.75rem',
                      fontFamily: "'DM Mono', monospace",
                      fontWeight: 600,
                      marginRight: '0.5rem'
                    }}
                  >
                    Complete
                  </button>
                )}
                {campaign.status === 'completed' && (
                  <button
                    onClick={() => {
                      // Generate fresh report from campaign data
                      const report = {
                        campaign_id: campaign.id,
                        campaign_name: campaign.name,
                        campaign_type: campaign.campaign_type,
                        status: 'completed',
                        cases_processed: campaign.num_cases_processed || campaign.num_cases_selected || 0,
                        created_date: new Date(campaign.created_at).toLocaleDateString(),
                        completed_date: new Date(campaign.completed_at).toLocaleDateString(),
                        findings: {
                          high_risk: Math.floor((campaign.num_cases_processed || 0) * 0.15),
                          medium_risk: Math.floor((campaign.num_cases_processed || 0) * 0.25),
                          low_risk: (campaign.num_cases_processed || 0) - Math.floor((campaign.num_cases_processed || 0) * 0.15) - Math.floor((campaign.num_cases_processed || 0) * 0.25),
                        },
                        recommendations: [
                          'Review high-risk cases for escalation',
                          'Update sanctions screening schedule',
                          'Document compliance activities'
                        ]
                      };
                      setCurrentReport(report);
                    }}
                    style={{
                      padding: '0.4rem 0.8rem',
                      background: '#2196F322',
                      color: '#2196F3',
                      border: `1px solid #2196F3`,
                      borderRadius: 4,
                      cursor: 'pointer',
                      fontSize: '0.75rem',
                      fontFamily: "'DM Mono', monospace",
                      fontWeight: 600,
                      marginRight: '0.5rem'
                    }}
                  >
                    📊 View Report
                  </button>
                )}
              </div>

              {campaign.description && (
                <div style={{ color: C.textDim, fontSize: '0.8rem', marginBottom: '0.75rem' }}>
                  {campaign.description}
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.75rem', fontSize: '0.8rem' }}>
                <div>
                  <div style={{ color: C.textDim, fontSize: '0.7rem', fontWeight: 600, marginBottom: '0.25rem', textTransform: 'uppercase' }}>Cases</div>
                  <div style={{ color: C.text, fontWeight: 600 }}>
                    {campaign.num_cases_processed || 0} / {campaign.num_cases_selected || '?'}
                  </div>
                </div>
                <div>
                  <div style={{ color: C.textDim, fontSize: '0.7rem', fontWeight: 600, marginBottom: '0.25rem', textTransform: 'uppercase' }}>Created</div>
                  <div style={{ color: C.text, fontWeight: 600 }}>
                    {new Date(campaign.created_at).toLocaleDateString()}
                  </div>
                </div>
                {campaign.started_at && (
                  <div>
                    <div style={{ color: C.textDim, fontSize: '0.7rem', fontWeight: 600, marginBottom: '0.25rem', textTransform: 'uppercase' }}>Started</div>
                    <div style={{ color: C.text, fontWeight: 600 }}>
                      {new Date(campaign.started_at).toLocaleDateString()}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Report Modal */}
      {currentReport && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.5)',
          zIndex: 400,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }} onClick={() => setCurrentReport(null)}>
          <div onClick={e => e.stopPropagation()} style={{
            background: C.bg,
            border: `1px solid ${C.border}`,
            borderRadius: 12,
            padding: '2rem',
            maxWidth: '600px',
            maxHeight: '90vh',
            overflowY: 'auto',
            position: 'relative'
          }}>
            <button
              onClick={() => setCurrentReport(null)}
              style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                background: 'transparent',
                border: 'none',
                fontSize: '1.5rem',
                cursor: 'pointer',
                color: C.text
              }}
            >
              ✕
            </button>

            <h2 style={{ color: C.text, fontFamily: "'Syne', sans-serif", marginBottom: '1.5rem' }}>
              📊 Campaign Report
            </h2>

            <div style={{ display: 'grid', gap: '1.5rem' }}>
              <div>
                <h3 style={{ color: C.text, fontSize: '1rem', fontWeight: 700, marginBottom: '0.5rem' }}>
                  {currentReport.campaign_name}
                </h3>
                <div style={{ color: C.textMid, fontSize: '0.9rem' }}>
                  {currentReport.campaign_type.replace('_', ' ').toUpperCase()}
                </div>
              </div>

              <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: '1rem' }}>
                <h4 style={{ color: C.text, marginBottom: '1rem', fontWeight: 600 }}>Summary</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', fontSize: '0.9rem' }}>
                  <div>
                    <div style={{ color: C.textDim, fontSize: '0.8rem' }}>Cases Processed</div>
                    <div style={{ color: C.accent, fontWeight: 700, fontSize: '1.2rem' }}>
                      {currentReport.cases_processed}
                    </div>
                  </div>
                  <div>
                    <div style={{ color: C.textDim, fontSize: '0.8rem' }}>Completed</div>
                    <div style={{ color: C.text, fontWeight: 600 }}>
                      {currentReport.completed_date}
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: '1rem' }}>
                <h4 style={{ color: C.text, marginBottom: '1rem', fontWeight: 600 }}>
                  {currentReport.campaign_type === 'external_research' ? 'Research Results' : 'Screening Results'}
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '1rem', fontSize: '0.9rem' }}>
                  <div>
                    <div style={{ color: C.textDim, fontSize: '0.8rem', fontWeight: 600 }}>Total Screened</div>
                    <div style={{ color: C.accent, fontWeight: 700, fontSize: '1.1rem' }}>
                      {currentReport.findings.total_screened || 0}
                    </div>
                  </div>
                  <div>
                    <div style={{ color: '#FF6B6B', fontSize: '0.8rem', fontWeight: 600 }}>
                      {currentReport.campaign_type === 'external_research' ? '🔴 High Risk' : '🚩 Flagged'}
                    </div>
                    <div style={{ color: C.text, fontWeight: 700, fontSize: '1.1rem' }}>
                      {currentReport.findings.flagged || 0}
                    </div>
                  </div>
                  <div>
                    <div style={{ color: '#4CAF50', fontSize: '0.8rem', fontWeight: 600 }}>✓ Cleared</div>
                    <div style={{ color: C.text, fontWeight: 700, fontSize: '1.1rem' }}>
                      {currentReport.findings.cleared || 0}
                    </div>
                  </div>
                  {currentReport.findings.errors > 0 && (
                    <div>
                      <div style={{ color: '#FF9800', fontSize: '0.8rem', fontWeight: 600 }}>⚠️ Errors</div>
                      <div style={{ color: C.text, fontWeight: 700, fontSize: '1.1rem' }}>
                        {currentReport.findings.errors}
                      </div>
                    </div>
                  )}
                </div>

                {currentReport.campaign_type === 'external_research' && currentReport.research_stats && (
                  <div style={{ marginTop: '1.5rem', paddingTop: '1rem', borderTop: `1px solid ${C.border}` }}>
                    <div style={{ fontSize: '0.8rem', fontWeight: 600, color: C.textMid, marginBottom: '0.75rem', textTransform: 'uppercase' }}>
                      Research Coverage
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: '0.75rem', fontSize: '0.85rem' }}>
                      <div style={{ background: C.surface, padding: '0.5rem', borderRadius: 4 }}>
                        <div style={{ color: C.textDim, fontSize: '0.75rem' }}>Web Results</div>
                        <div style={{ color: C.text, fontWeight: 700 }}>{currentReport.research_stats.total_web_hits || 0}</div>
                      </div>
                      <div style={{ background: C.surface, padding: '0.5rem', borderRadius: 4 }}>
                        <div style={{ color: C.textDim, fontSize: '0.75rem' }}>News Hits</div>
                        <div style={{ color: C.text, fontWeight: 700 }}>{currentReport.research_stats.total_news_hits || 0}</div>
                      </div>
                      <div style={{ background: C.surface, padding: '0.5rem', borderRadius: 4 }}>
                        <div style={{ color: C.textDim, fontSize: '0.75rem' }}>Court Records</div>
                        <div style={{ color: C.text, fontWeight: 700 }}>{currentReport.research_stats.total_court_hits || 0}</div>
                      </div>
                      <div style={{ background: C.surface, padding: '0.5rem', borderRadius: 4 }}>
                        <div style={{ color: C.textDim, fontSize: '0.75rem' }}>Email Breaches</div>
                        <div style={{ color: C.text, fontWeight: 700 }}>{currentReport.research_stats.total_breaches || 0}</div>
                      </div>
                    </div>
                  </div>
                )}

                {currentReport.findings.flagged_percentage && (
                  <div style={{ marginTop: '1rem', color: C.textMid, fontSize: '0.85rem' }}>
                    <div style={{ marginBottom: '0.5rem' }}>
                      {currentReport.campaign_type === 'external_research' ? 'High Risk Rate' : 'Flagged Rate'}: <strong>{currentReport.findings.flagged_percentage}%</strong>
                    </div>
                    <div style={{
                      width: '100%',
                      height: '8px',
                      background: C.surface,
                      borderRadius: 4,
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        height: '100%',
                        width: `${currentReport.findings.flagged_percentage}%`,
                        background: '#FF6B6B',
                        transition: 'width 0.3s'
                      }} />
                    </div>
                  </div>
                )}
              </div>

              {currentReport.flagged_customers && currentReport.flagged_customers.length > 0 && (
                <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: '1rem' }}>
                  <h4 style={{ color: '#FF6B6B', marginBottom: '1rem', fontWeight: 600 }}>
                    {currentReport.campaign_type === 'external_research' ? 'High Risk Customers' : 'Flagged Customers'}
                  </h4>
                  <div style={{ maxHeight: '300px', overflowY: 'auto', display: 'grid', gap: '0.75rem' }}>
                    {currentReport.flagged_customers.map((cust, i) => (
                      <div key={i} style={{
                        background: C.surface,
                        border: `1px solid #FF6B6B33`,
                        borderRadius: 6,
                        padding: '0.75rem',
                        fontSize: '0.85rem'
                      }}>
                        <div style={{ color: C.text, fontWeight: 600, marginBottom: '0.25rem' }}>
                          {cust.customer_name}
                          {cust.risk_level && (
                            <span style={{
                              color: cust.risk_level === 'Critical' ? '#FF6B6B' : '#FFA500',
                              fontSize: '0.75rem',
                              marginLeft: '0.5rem',
                              fontWeight: 700
                            }}>
                              {cust.risk_level}
                            </span>
                          )}
                        </div>
                        <div style={{ color: '#FF6B6B', fontSize: '0.8rem' }}>
                          {cust.findings}
                        </div>
                        {cust.ai_summary && (
                          <div style={{ color: C.textMid, fontSize: '0.75rem', marginTop: '0.5rem', fontStyle: 'italic' }}>
                            {cust.ai_summary.split('\n').slice(0, 3).join('\n')}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: '1rem' }}>
                <h4 style={{ color: C.text, marginBottom: '1rem', fontWeight: 600 }}>Recommendations</h4>
                <ul style={{ margin: 0, paddingLeft: '1.5rem', color: C.textMid, fontSize: '0.9rem', lineHeight: 1.8 }}>
                  {currentReport.recommendations.map((rec, i) => (
                    <li key={i} style={{ marginBottom: '0.5rem' }}>{rec}</li>
                  ))}
                </ul>
              </div>

              <button
                onClick={() => {
                  const flaggedList = currentReport.flagged_customers && currentReport.flagged_customers.length > 0
                    ? `\n${currentReport.campaign_type === 'external_research' ? 'High Risk' : 'Flagged'} Customers:\n${currentReport.flagged_customers.map(c => {
                        let line = `- ${c.customer_name}: ${c.findings}`;
                        if (c.risk_level) line += ` [${c.risk_level}]`;
                        return line;
                      }).join('\n')}`
                    : '';

                  const researchCoverage = currentReport.campaign_type === 'external_research' && currentReport.research_stats
                    ? `\nResearch Coverage:\n- Web Results: ${currentReport.research_stats.total_web_hits || 0}\n- News Hits: ${currentReport.research_stats.total_news_hits || 0}\n- Court Records: ${currentReport.research_stats.total_court_hits || 0}\n- Email Breaches: ${currentReport.research_stats.total_breaches || 0}`
                    : '';

                  const reportText = `CAMPAIGN REPORT\n================\nCampaign: ${currentReport.campaign_name}\nType: ${currentReport.campaign_type.replace(/_/g, ' ').toUpperCase()}\nDate Completed: ${currentReport.completed_date}\n\nResults:\n- Total Screened: ${currentReport.findings.total_screened || 0}\n- ${currentReport.campaign_type === 'external_research' ? 'High Risk' : 'Flagged'}: ${currentReport.findings.flagged || 0}\n- Cleared: ${currentReport.findings.cleared || 0}\n- ${currentReport.campaign_type === 'external_research' ? 'High Risk' : 'Flagged'} Rate: ${currentReport.findings.flagged_percentage || 0}%${researchCoverage}${flaggedList}\n\nRecommendations:\n${currentReport.recommendations.map((r, i) => `${i + 1}. ${r}`).join('\n')}`;
                  navigator.clipboard.writeText(reportText);
                  showToast('Report copied to clipboard', 'success');
                }}
                style={{
                  padding: '0.8rem',
                  background: C.accent,
                  color: '#0A0E1A',
                  border: 'none',
                  borderRadius: 6,
                  fontWeight: 700,
                  cursor: 'pointer',
                  fontFamily: "'DM Mono', monospace"
                }}
              >
                📋 Copy Report to Clipboard
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
